// 全局认证状态管理
import { checkCertificationStatus, isLoggedIn } from './auth.js'
import { checkWorkingTimeAsync, getCurrentTimeString } from './timeCheck.js'
import { getTradingStatus } from './tradingTimeService.js'

class CertificationManager {
  constructor() {
    this.certificationState = null
    this.userInfo = null
    this.isChecking = false
    this.lastCheckTime = 0
    this.checkInterval = 5 * 60 * 1000 // 5分钟检查一次
  }

  /**
   * 获取认证状态（带缓存）
   * @param {boolean} forceRefresh - 是否强制刷新
   * @returns {Promise<Object>} 认证状态信息
   */
  async getCertificationStatus(forceRefresh = false) {
    const now = Date.now()
    
    // 如果有缓存且未过期，直接返回
    if (!forceRefresh && 
        this.certificationState !== null && 
        (now - this.lastCheckTime) < this.checkInterval) {
      return {
        certificationState: this.certificationState,
        userInfo: this.userInfo
      }
    }

    // 如果正在检查中，等待检查完成
    if (this.isChecking) {
      return new Promise((resolve) => {
        const checkComplete = () => {
          resolve({
            certificationState: this.certificationState,
            userInfo: this.userInfo
          })
        }
        
        // 等待最多3秒
        setTimeout(checkComplete, 3000)
      })
    }

    this.isChecking = true
    
    try {
      const result = await checkCertificationStatus()
      // 如果用户被冻结，不更新认证状态，避免触发后续认证检查
      if (result.isFrozen) {
        // 用户被冻结，保持当前状态，不触发认证检查
        return {
          certificationState: this.certificationState || 0,
          userInfo: this.userInfo || null,
          isFrozen: true
        }
      }
      this.certificationState = result.certificationState
      this.userInfo = result.userInfo
      this.lastCheckTime = now
      
      return result
    } catch (error) {
      console.error('获取认证状态失败:', error)
      // 如果是403错误，不触发认证检查
      if (error?.response?.status === 403 || error?.response?.data?.code === 403) {
        return {
          certificationState: this.certificationState || 0,
          userInfo: this.userInfo || null,
          isFrozen: true
        }
      }
      return {
        certificationState: 0,
        userInfo: null
      }
    } finally {
      this.isChecking = false
    }
  }

  /**
   * 检查是否需要认证并处理（不自动弹窗）
   * @param {string} currentPath - 当前路径
   * @param {Object} router - Vue Router实例
   */
  async checkAndHandleCertification(currentPath, router) { // eslint-disable-line no-unused-vars
    // 使用统一的登录状态检查
    if (!isLoggedIn()) {
      return false // 需要登录
    }
    
    const status = await this.getCertificationStatus()
    
    // 如果用户被冻结，不触发认证检查
    if (status.isFrozen) {
      return true // 用户被冻结，不触发认证检查
    }
    
    // 认证状态：0：未认证，1：认证通过, 2：认证待审核，3：认证不通过
    // 使用更宽松的比较，处理字符串和数字类型
    const certState = Number(status.certificationState)
    // 仅当明确为“未认证”或“认证不通过”时，按未认证处理
    if (certState === 0 || certState === 3) {
      const requiresCertification = this.isPathRequiringCertification(currentPath)
      
      if (requiresCertification) {
        // 只返回状态，不自动弹窗，由调用方决定如何处理
        return false // 需要认证
      }
    }
    
    return true // 不需要认证或已认证
  }

  /**
   * 获取认证信息并跳转到对应步骤
   * @param {Object} router - Vue Router实例
   * @returns {Promise<void>}
   */
  async getCertificationInfoAndNavigate(router) {
    try {
      const { get } = await import('@/utils/axios')
      const res = await get('/app/cert/info')
      const data = res?.data?.data || res?.data || {}
      const step = Number(data.step || 0)
      const certId = data.id || ''

      // 步骤：1身份证 2执照 3法大大已授权 4合同 5门店完成(成功页)
      let path = '/cert/idCard'
      if (step === 1) path = '/cert/idCard'
      else if (step === 2) path = '/cert/license'
      else if (step === 3) path = '/cert/contract'
      else if (step === 4) path = '/cert/store'
      else if (step >= 5) path = '/cert/success'

      if (certId) {
        router.push({ path, query: { id: certId } })
      } else {
        router.push(path)
      }
    } catch (e) {
      console.error('获取认证信息失败:', e)
      // 获取失败时回退到身份证认证
      router.push('/cert/idCard')
    }
  }

  /**
   * 判断路径是否需要认证
   * @param {string} path - 路径
   * @returns {boolean} 是否需要认证
   */
  isPathRequiringCertification(path) {
    // 需要认证的路径列表
    const certificationRequiredPaths = [
      '/order',
      '/my/order',
      '/address',
      '/my/bank_card',
      '/my/settings'
    ]
    
    // 不需要认证的路径列表
    const certificationExemptPaths = [
      '/login',
      '/cert',
      '/price',
      '/lossless',
      '/damage',
      '/admin/login'
    ]
    
    // 检查是否在豁免列表中
    for (const exemptPath of certificationExemptPaths) {
      if (path.startsWith(exemptPath)) {
        return false
      }
    }
    
    // 检查是否在需要认证的列表中
    for (const requiredPath of certificationRequiredPaths) {
      if (path.startsWith(requiredPath)) {
        return true
      }
    }
    
    return false
  }

  /**
   * 清除缓存
   */
  clearCache() {
    this.certificationState = null
    this.userInfo = null
    this.lastCheckTime = 0
  }

  /**
   * 强制刷新认证状态（清除缓存并重新获取）
   */
  async forceRefresh() {
    this.clearCache()
    return await this.getCertificationStatus(true)
  }

  /**
   * 设置认证状态
   * @param {number} state - 认证状态 0:未认证 1:已认证
   * @param {Object} userInfo - 用户信息
   */
  setCertificationState(state, userInfo = null) {
    this.certificationState = state
    this.userInfo = userInfo
    this.lastCheckTime = Date.now()
    
    // 触发全局事件，通知其他组件用户信息已更新
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('userInfoUpdated', {
        detail: { userInfo, certificationState: state }
      }))
    }
  }

  /**
   * 统一的"立即卖金"按钮处理函数
   * 整合所有检查逻辑：工作时间、登录状态、认证状态、用户状态
   * @param {Object} options - 配置选项
   * @param {Object} options.router - Vue Router实例（可选，用于跳转登录页）
   * @param {Function} options.onWorkingTimeCheck - 工作时间检查回调，接收 { message, currentTime } 参数
   * @param {Function} options.onCertificationCheck - 认证检查回调，无参数
   * @param {string} options.redirectPath - 跳转路径，默认为 '/order/index'
   * @returns {Promise<Object>} 返回结果对象 { canProceed: boolean, action: string, data: any }
   */
  async handleSellGoldClick(options = {}) {
    const {
      router = null,
      onWorkingTimeCheck = null,
      onCertificationCheck = null,
      redirectPath = '/order/index'
    } = options

    // 1. 检查工作时间（使用接口返回的信息）
    const timeCheck = await checkWorkingTimeAsync()
    if (!timeCheck.isWorkingTime) {
      const currentTime = getCurrentTimeString()
      // 获取交易时间配置信息
      let tradingTimeConfig = null
      let isHoliday = false
      try {
        const tradingStatus = await getTradingStatus()
        tradingTimeConfig = tradingStatus.config
        isHoliday = tradingStatus.isHoliday || false
      } catch (error) {
        console.warn('获取交易时间配置失败:', error)
      }
      
      if (onWorkingTimeCheck) {
        onWorkingTimeCheck({
          message: timeCheck.message,
          currentTime: currentTime,
          tradingTimeConfig: tradingTimeConfig,
          isHoliday: isHoliday
        })
      }
      return {
        canProceed: false,
        action: 'showWorkingTimeDialog',
        data: {
          message: timeCheck.message,
          currentTime: currentTime,
          tradingTimeConfig: tradingTimeConfig,
          isHoliday: isHoliday
        }
      }
    }

    // 2. 检查登录状态
    if (!isLoggedIn()) {
      if (router) {
        router.push({
          path: '/login',
          query: { redirect: redirectPath }
        })
      }
      return {
        canProceed: false,
        action: 'redirectToLogin',
        data: { redirectPath }
      }
    }

    // 3. 检查认证状态
    try {
      const status = await this.getCertificationStatus(true)
      // 如果用户被冻结，不继续执行后续检查
      if (status.isFrozen) {
        return {
          canProceed: false,
          action: 'userFrozen',
          data: {}
        }
      }
      const certState = Number(status.certificationState)
      
      // 未认证：走统一的认证弹框流程
      if (certState === 0) {
        const requiresCertification = this.isPathRequiringCertification(redirectPath)
        if (requiresCertification) {
          if (onCertificationCheck) {
            onCertificationCheck()
          }
          return {
            canProceed: false,
            action: 'showCertificationDialog',
            data: {}
          }
        }
      }

      // 认证不通过：弹出失败原因弹框，引导去重新提交
      if (certState === 3) {
        await this.showCertificationFailedDialog({
          router,
          reason: status?.userInfo?.remark
        })
        return {
          canProceed: false,
          action: 'certificationFailed',
          data: {}
        }
      }
    } catch (error) {
      console.error('认证状态检查失败:', error)
      // 如果是403错误，不触发认证检查
      if (error?.response?.status === 403 || error?.response?.data?.code === 403) {
        return {
          canProceed: false,
          action: 'userFrozen',
          data: {}
        }
      }
      // 出错时默认需要认证
      if (onCertificationCheck) {
        onCertificationCheck()
      }
      return {
        canProceed: false,
        action: 'showCertificationDialog',
        data: {}
      }
    }

    // 4. 检查用户状态和认证状态
    const canSell = await this.checkUserStatusBeforeSell()
    if (!canSell) {
      return {
        canProceed: false,
        action: 'userStatusBlocked',
        data: {}
      }
    }

    // 5. 检查老用户是否需要签署补充协议
    const supplementalCheck = await this.checkSupplementalAgreement()
    if (!supplementalCheck.canProceed) {
      return {
        canProceed: false,
        action: 'needSupplementalAgreement',
        data: supplementalCheck.data
      }
    }

    // 6. 所有检查通过：清空上次下单缓存后再放行跳转
    try {
      const { resetOrderFlowSession } = await import('@/utils/orderFee.js')
      resetOrderFlowSession()
    } catch (e) {
      console.warn('[handleSellGoldClick] 清空下单缓存失败:', e)
    }
    return {
      canProceed: true,
      action: 'redirect',
      data: { path: redirectPath }
    }
  }

  /**
   * 检查老用户是否需要签署补充协议
   * 老用户（isLegacyUser = 1）在卖金前需要签署补充协议
   * @returns {Promise<Object>} 返回结果对象 { canProceed: boolean, data: any }
   */
  async checkSupplementalAgreement() {
    try {
      const { get } = await import('@/utils/axios')
      const res = await get('/app/fadada/checkSupplementalAgreement')
      const data = res?.data?.data || {}

      // 不需要签署（非老用户或已签署）
      if (!data.needSign) {
        return { canProceed: true, data: null }
      }

      // 需要签署补充协议
      return {
        canProceed: false,
        data: {
          signUrl: data.signUrl,
          isLegacyUser: data.isLegacyUser,
          signStatus: data.signStatus
        }
      }
    } catch (error) {
      console.error('检查补充协议状态失败:', error)
      // 接口异常时默认允许继续，避免阻塞用户
      return { canProceed: true, data: null }
    }
  }

  /**
   * 操作前认证检查（不含工作时间检查）
   * 用于会员购买等需实名认证的场景
   * @param {Object} options
   * @param {Object} options.router - Vue Router 实例
   * @param {Function} options.onCertificationCheck - 未认证时回调，用于弹出认证弹框
   * @returns {Promise<boolean>} 是否允许继续
   */
  async checkCertificationBeforeAction(options = {}) {
    const {
      router = null,
      onCertificationCheck = null
    } = options

    if (!isLoggedIn()) {
      if (router) {
        router.push({
          path: '/login',
          query: { redirect: router.currentRoute?.value?.fullPath || '/my/member/index' }
        })
      }
      return false
    }

    try {
      const status = await this.getCertificationStatus(true)
      if (status.isFrozen) {
        return false
      }

      const certState = Number(status.certificationState ?? 0)
      if (certState === 0) {
        if (onCertificationCheck) {
          onCertificationCheck()
        }
        return false
      }
      if (certState === 2) {
        await this.showAuditUrgeDialog()
        return false
      }
      if (certState === 3) {
        await this.showCertificationFailedDialog({
          router,
          reason: status?.userInfo?.remark
        })
        return false
      }
    } catch (error) {
      console.error('认证状态检查失败:', error)
      if (error?.response?.status === 403 || error?.response?.data?.code === 403) {
        return false
      }
      if (onCertificationCheck) {
        onCertificationCheck()
      }
      return false
    }

    return this.checkUserStatusBeforeSell()
  }

  /**
   * 检查用户状态和认证状态，判断是否允许进行卖金操作
   * @returns {Promise<boolean>} 返回 true 表示允许跳转，false 表示不允许
   */
  async checkUserStatusBeforeSell() {
    try {
      // 获取认证状态和用户信息
      const certStatus = await this.getCertificationStatus(true)
      // 如果用户被冻结，不继续执行后续检查
      if (certStatus.isFrozen) {
        return false // 用户被冻结，阻止跳转
      }
      const certificationState = Number(certStatus.certificationState || 0)
      let userStatus = null
      
      // 从用户信息中获取用户状态
      if (certStatus.userInfo) {
        userStatus = certStatus.userInfo.status || null
      }
      
      // 如果用户信息中没有status，尝试从认证信息接口获取
      if (userStatus === null) {
        try {
          const { get } = await import('@/utils/axios')
          const res = await get('/app/cert/info')
          const data = res?.data?.data || res?.data || {}
          if (data.status !== undefined) {
            userStatus = Number(data.status)
          }
        } catch (e) {
          // 忽略错误
        }
      }
      
      // 1. 用户状态为0时，显示账户异常提示（账户冻结/异常，与认证无关）
      if (userStatus === 0) {
        await this.showAccountAbnormalDialog()
        return false // 阻止跳转
      }

      // 2. 认证状态为2（认证待审核）时，提示审核中，显示“催一催”
      if (certificationState === 2) {
        await this.showAuditUrgeDialog()
        return false // 阻止跳转
      }

      // 3. 认证状态为0（未认证）或3（认证不通过）时，提示需要认证
      if (certificationState === 0 || certificationState === 3) {
        const { CERTIFICATION_MESSAGES } = await import('@/config')
        await this.showInfoDialog(
          CERTIFICATION_MESSAGES.NEED_CERTIFICATION_FOR_SELL,
          CERTIFICATION_MESSAGES.NOT_CERTIFIED_TITLE
        )
        return false // 阻止跳转
      }
      
      // 4. 认证状态为1（认证通过）时，才能跳转到下单页
      if (certificationState === 1) {
        return true // 允许跳转
      }
      
      // 5. 其他状态，根据具体情况提示用户
      let message = ''
      let title = '提示'
      
      if (userStatus === null || userStatus === undefined) {
        message = '无法获取您的认证状态，请稍后重试'
        title = '获取状态失败'
      } else {
        message = '您的账户状态暂不符合卖金要求，请联系客服咨询'
        title = '状态不符合要求'
      }
      
      await this.showInfoDialog(message, title)
      return false // 阻止跳转
    } catch (error) {
      console.error('检查用户状态失败:', error)
      // 出错时允许跳转，避免影响正常流程
      return true
    }
  }

  /**
   * 显示账户异常对话框（用户状态为0时使用）
   * @returns {Promise<void>}
   */
  async showAccountAbnormalDialog() {
    return new Promise((resolve) => {
      // 动态创建 Vue 应用和组件实例
      Promise.all([
        import('vue'),
        import('@/components/InfoDialog.vue')
      ]).then(([{ createApp, h }, { default: InfoDialog }]) => {
        // 创建容器
        const container = document.createElement('div')
        document.body.appendChild(container)
        
        // 创建 Vue 应用
        const app = createApp({
          data() {
            return {
              visible: true
            }
          },
          methods: {
            handleClose() {
              this.visible = false
              setTimeout(() => {
                app.unmount()
                if (container.parentNode) {
                  document.body.removeChild(container)
                }
                resolve()
              }, 300)
            }
          },
          render() {
            return h(InfoDialog, {
              visible: this.visible,
              title: '提示',
              message: '您的账户存在异常，请联系客户人员',
              confirmText: '我知道了',
              closeOnClickModal: false,
              onConfirm: this.handleClose,
              onClose: this.handleClose
            })
          }
        })
        
        app.mount(container)
      }).catch(() => {
        // 降级处理：使用 alert
        alert('提示\n\n您的账户存在异常，请联系客户人员')
        resolve()
      })
    })
  }

  /**
   * 显示认证失败对话框（状态为3时使用）
   * @param {Object} options
   * @param {Object} options.router - Vue Router 实例
   * @param {string} options.reason - 失败原因（可选）
   * @returns {Promise<void>}
   */
  async showCertificationFailedDialog({ router = null, reason = '' } = {}) {
    const failReason = reason || '很抱歉，您的认证未通过，请根据提示重新提交相关资料。'
    return new Promise((resolve) => {
      Promise.all([
        import('vue'),
        import('@/components/InfoDialog.vue')
      ]).then(([{ createApp, h }, { default: InfoDialog }]) => {
        const container = document.createElement('div')
        document.body.appendChild(container)

        const app = createApp({
          data() {
            return {
              visible: true
            }
          },
          methods: {
            handleCancel() {
              this.visible = false
              setTimeout(() => {
                app.unmount()
                if (container.parentNode) {
                  document.body.removeChild(container)
                }
                resolve()
              }, 300)
            },
            handleConfirm() {
              // 先关闭弹框
              this.visible = false
              setTimeout(() => {
                app.unmount()
                if (container.parentNode) {
                  document.body.removeChild(container)
                }
                // 再跳转到认证失败页
                try {
                  if (router && router.push) {
                    router.push('/cert/fail')
                  } else if (typeof window !== 'undefined') {
                    window.location.hash = '#/cert/fail'
                  }
                } catch (e) {
                  if (typeof window !== 'undefined') {
                    window.location.hash = '#/cert/fail'
                  }
                }
                resolve()
              }, 300)
            }
          },
          render() {
            return h(InfoDialog, {
              visible: this.visible,
              title: '认证失败',
              message: failReason,
              confirmText: '重新提交',
              cancelText: '取消',
              showCancel: true,
              closeOnClickModal: false,
              onConfirm: this.handleConfirm,
              onCancel: this.handleCancel,
              onClose: this.handleCancel
            })
          }
        })

        app.mount(container)
      }).catch(() => {
        // 降级处理：使用 confirm
        const go = window.confirm(`${failReason}\n\n点击“确定”前往重新提交认证`)
        if (go) {
          try {
            if (router && router.push) {
              router.push('/cert/fail')
            } else if (typeof window !== 'undefined') {
              window.location.hash = '#/cert/fail'
            }
          } catch (e) {
            if (typeof window !== 'undefined') {
              window.location.hash = '#/cert/fail'
            }
          }
        }
        resolve()
      })
    })
  }

  /**
   * 显示审核催一催对话框
   * @returns {Promise<void>}
   */
  async showAuditUrgeDialog() {
    return new Promise((resolve) => {
      // 动态创建 Vue 应用和组件实例
      Promise.all([
        import('vue'),
        import('@/components/InfoDialog.vue'),
        import('@/utils/axios')
      ]).then(([{ createApp, h }, { default: InfoDialog }, { post }]) => {
        // 创建容器
        const container = document.createElement('div')
        document.body.appendChild(container)
        
        let dialogInstance = null
        
        // 创建 Vue 应用
        const app = createApp({
          data() {
            return {
              visible: true,
              loading: false
            }
          },
          methods: {
            async handleUrge() {
              if (this.loading) return
              
              this.loading = true
              if (dialogInstance) {
                dialogInstance.setLoading(true)
              }
              
              try {
                const res = await post('/app/msg/urge-audit')
                const data = res?.data || {}
                
                // 成功提示 - 简化文字
                const successMsg = data.msg || data.message || '催审消息已发送'
                
                // 使用 ElMessage 显示成功提示，使用 info 类型（蓝色）而不是 success（绿色）
                import('element-plus').then(({ ElMessage }) => {
                  ElMessage({
                    message: successMsg,
                    type: 'info',
                    duration: 3000,
                    showClose: false
                  })
                }).catch(() => {
                  alert(successMsg)
                })
                
                // 关闭对话框
                this.handleClose()
              } catch (error) {
                // 处理错误
                let errorMsg = '催审失败，请稍后重试'
                
                if (error?.response?.data) {
                  const errorData = error.response.data
                  errorMsg = errorData.msg || errorData.message || errorMsg
                } else if (error?.message) {
                  errorMsg = error.message
                }
                
                // 使用 ElMessage 显示错误提示
                import('element-plus').then(({ ElMessage }) => {
                  ElMessage.error(errorMsg)
                }).catch(() => {
                  alert(errorMsg)
                })
                
                // 保持对话框打开，让用户可以重试或取消
                this.loading = false
                if (dialogInstance) {
                  dialogInstance.setLoading(false)
                }
              }
            },
            
            handleCancel() {
              this.handleClose()
            },
            
            handleClose() {
              this.visible = false
              setTimeout(() => {
                app.unmount()
                if (container.parentNode) {
                  document.body.removeChild(container)
                }
                resolve()
              }, 300)
            }
          },
          render() {
            return h(InfoDialog, {
              ref: (el) => { if (el) dialogInstance = el },
              visible: this.visible,
              title: '审核中',
              message: '您的认证信息正在审核中，审核通过后即可进行卖金操作',
              confirmText: '催一催',
              cancelText: '取消',
              showUrgeButton: true,
              closeOnClickModal: false,
              onUrge: this.handleUrge,
              onCancel: this.handleCancel,
              onClose: this.handleClose
            })
          }
        })
        
        app.mount(container)
      }).catch(() => {
        // 降级处理：使用 alert
        alert('审核中\n\n您的认证信息正在审核中，审核通过后即可进行卖金操作，请耐心等待')
        resolve()
      })
    })
  }

  /**
   * 显示联系客服对话框
   * @returns {Promise<void>}
   */
  async showCustomerServiceDialog() {
    return new Promise((resolve) => {
      // 动态创建 Vue 应用和组件实例
      Promise.all([
        import('vue'),
        import('@/components/CustomerServiceDialog.vue')
      ]).then(([{ createApp, h }, { default: CustomerServiceDialog }]) => {
        // 创建容器
        const container = document.createElement('div')
        document.body.appendChild(container)
        
        // 创建 Vue 应用
        const app = createApp({
          data() {
            return {
              visible: true
            }
          },
          methods: {
            handleClose() {
              this.visible = false
              setTimeout(() => {
                app.unmount()
                if (container.parentNode) {
                  document.body.removeChild(container)
                }
                resolve()
              }, 300)
            },
            handleCall() {
              // 拨打电话的逻辑在组件内部处理
            }
          },
          render() {
            return h(CustomerServiceDialog, {
              visible: this.visible,
              closeOnClickModal: false,
              onClose: this.handleClose,
              onCall: this.handleCall
            })
          }
        })
        
        app.mount(container)
      }).catch(() => {
        // 降级处理：使用 alert
        import('@/config').then(({ CUSTOMER_SERVICE }) => {
          alert(`联系客服\n\n客服工号：${CUSTOMER_SERVICE.SERVICE_NUMBER}\n联系电话：${CUSTOMER_SERVICE.PHONE_NUMBER}`)
        }).catch(() => {
          alert('联系客服\n\n客服工号：0001\n联系电话：19980859372')
        })
        resolve()
      })
    })
  }

  /**
   * 显示信息框
   * @param {string} message - 提示消息
   * @param {string} title - 标题，默认为'提示'
   * @param {Object} options - 选项配置
   * @param {boolean} options.showCustomerServiceButton - 是否显示联系客服按钮
   * @returns {Promise<void>}
   */
  async showInfoDialog(message, title = '提示', options = {}) {
    return new Promise((resolve) => {
      // 动态创建 Vue 应用和组件实例
      Promise.all([
        import('vue'),
        import('@/components/InfoDialog.vue')
      ]).then(([{ createApp, h }, { default: InfoDialog }]) => {
        // 创建容器
        const container = document.createElement('div')
        document.body.appendChild(container)
        
        // 创建 Vue 应用
        const app = createApp({
          data() {
            return {
              visible: true
            }
          },
          methods: {
            handleClose() {
              this.visible = false
              setTimeout(() => {
                app.unmount()
                if (container.parentNode) {
                  document.body.removeChild(container)
                }
                resolve()
              }, 300)
            },
            handleConfirm() {
              // 如果是联系客服按钮，先关闭当前对话框，然后显示联系客服对话框
              if (options.showCustomerServiceButton) {
                this.handleCustomerService()
              } else {
                this.handleClose()
              }
            },
            async handleCustomerService() {
              // 关闭当前对话框
              this.handleClose()
              // 等待当前对话框关闭动画完成
              await new Promise(resolve => setTimeout(resolve, 300))
              // 显示联系客服对话框
              await certificationManager.showCustomerServiceDialog()
            }
          },
          render() {
            return h(InfoDialog, {
              visible: this.visible,
              title: title,
              message: message,
              confirmText: options.showCustomerServiceButton ? '联系客服' : '我知道了',
              cancelText: options.showCustomerServiceButton ? '我知道了' : '取消',
              showCustomerServiceButton: options.showCustomerServiceButton || false,
              closeOnClickModal: false,
              onConfirm: this.handleConfirm,
              onCancel: this.handleClose,
              onCustomerService: this.handleCustomerService,
              onClose: this.handleClose
            })
          }
        })
        
        app.mount(container)
      }).catch(() => {
        // 降级处理：使用 alert
        alert(`${title}\n\n${message}`)
        resolve()
      })
    })
  }
}

// 创建全局实例
const certificationManager = new CertificationManager()

// Vue插件
export default {
  install(app) {
    // 全局属性
    app.config.globalProperties.$certification = certificationManager
    
    // 全局方法
    app.config.globalProperties.$checkCertification = async (path) => {
      return await certificationManager.checkAndHandleCertification(
        path || window.location.pathname, 
        app.config.globalProperties.$router
      )
    }
  }
}

// 导出实例和方法
export { certificationManager }
export const checkCertification = (path) => 
  certificationManager.checkAndHandleCertification(path, null)
export const getCertificationStatus = (forceRefresh) => 
  certificationManager.getCertificationStatus(forceRefresh)
export const checkUserStatusBeforeSell = () => 
  certificationManager.checkUserStatusBeforeSell()
export const handleSellGoldClick = (options) => 
  certificationManager.handleSellGoldClick(options)
export const checkCertificationBeforeAction = (options) =>
  certificationManager.checkCertificationBeforeAction(options)
